// Components==============
import DragNDropTile from 'components/UI/DragNDropTile/DragNDropTile';
import { SoundType } from 'components/UI/LetterBlock/LetterBlock';
import { BoxTypes } from 'components/UI/StatusBox/StatusBox';
import React, { useContext, useEffect, useState } from 'react';
import { compareArray } from 'utilities/compareArray';
import { deConvertArray } from 'utilities/convertArray';
import {
    SoundGroupExerciseContext,
    UserAnswerType,
} from '../../SoundGroupExercise';
import './Group.module.scss';
// =========================

const tiles = Array.from(Array(5).keys());
export const defaultGroupTileBarStateValue: SoundType[] = Array(5).fill('');

export default function Group({
    boxState,
    setBoxState,
    id,
    name,
    index,
    blockSize,
    alreadyAnswered,
}: {
    boxState: BoxTypes;
    setBoxState: React.Dispatch<React.SetStateAction<BoxTypes>>;
    id: string;
    name: string;
    index: number;
    blockSize: string;
    alreadyAnswered?: UserAnswerType;
}) {
    const {
        userAnswers,
        current,
        dragState,
        answer,
        setActiveTileBar,
        setActiveTileBarState,
        activeTileBar,
        activeTileBarState,
        useInput,
        toggleIsUpdatedOnClick,
        setCurrentAnswer,
        currentAnswer,
        givenAnswers,
    } = useContext(SoundGroupExerciseContext);

    const [tileBarState, setTileBarState] = useState(
        defaultGroupTileBarStateValue
    );

    // set values if answer is already given
    useEffect(() => {
        if (alreadyAnswered?.correctBars?.[index]) {
            const convertedArray = deConvertArray(
                alreadyAnswered?.correctBars?.[index],
                true
            );

            setTileBarState(convertedArray);
            return;
        }
        setTileBarState(defaultGroupTileBarStateValue);
    }, [alreadyAnswered, name]); // name is added here to fix bug when user has slow network connection and alreadyAnswered would not exists yet when the user goes to next word. In that case the this useeffect would not fire. That caused the exercise to get stuck because this component would not reset to its initial state

    const item = dragState?.draggableId!;
    const source = dragState?.source.droppableId;
    const destination = dragState?.destination?.droppableId;
    const destinationIndex = parseInt(destination?.charAt(4) || '-1');
    const sourceIndex = parseInt(source?.charAt(4) || '-1');

    useEffect(() => {
        if (destinationIndex > -1 && destination?.includes(id)) {
            // Add item
            if (source === 'bar') {
                return setTileBarState((prev) =>
                    prev.map((e, i) => {
                        if (i === destinationIndex) return item as SoundType;
                        return e;
                    })
                );
            }

            // Replace item
            return setTileBarState((prev) =>
                prev.map((e, i) => {
                    const prevGroup =
                        currentAnswer[parseInt(source!.charAt(12))];
                    if (!compareArray(prevGroup, prev)) return e;

                    if (i === destinationIndex && i === sourceIndex) return e;

                    if (i === destinationIndex) {
                        return tileBarState[sourceIndex];
                    }
                    if (i === sourceIndex) return '';
                    return e;
                })
            );
        }

        // COMES WITH THE FOLLOWING BUG -> DRAG DELETE -> CLICK ADD ITEM -> BAR CLEARS
        // // Delete item
        // if (!destination && source !== 'bar') {
        //     return setTileBarState((prev) =>
        //         prev.map((e, i) => {
        //             if (i === sourceIndex) return '';
        //             return e;
        //         })
        //     );
        // }
    }, [dragState]);

    const tilePartIsFinished = boxState === 'correct' || boxState === 'failed';

    useEffect(() => {
        if (alreadyAnswered) return;

        const resetTileBarState = () => {
            setActiveTileBarState(defaultGroupTileBarStateValue);
            setActiveTileBar('initial');
        };

        if (tilePartIsFinished && !useInput) return resetTileBarState();

        if (
            typeof tileBarState.find((e) => e !== '') !== 'undefined' &&
            !tilePartIsFinished
            // !text
        ) {
            setBoxState('submit');
            setActiveTileBarState(tileBarState);
            return setActiveTileBar(id);
        }
        resetTileBarState();
    }, [tileBarState, tilePartIsFinished]);

    useEffect(() => {
        if (
            typeof tileBarState.find((e) => e !== '') !== 'undefined' &&
            activeTileBar === id
        ) {
            setTileBarState(activeTileBarState);
        }
    }, [toggleIsUpdatedOnClick]);

    useEffect(() => {
        // dont setCurrent if is completed
        if (alreadyAnswered) return;

        setCurrentAnswer((prev) =>
            prev.map((a, i) => (i === index ? tileBarState : a))
        );
    }, [tileBarState]);

    useEffect(() => {
        if (givenAnswers) return;
        if (boxState === 'failed') {
            const convertedArray = deConvertArray(
                answer?.klankgroepen[index],
                true
            );
            setTileBarState(convertedArray);
        }
    }, [boxState]);

    const answerIsFilledIn = userAnswers[current] !== undefined;
    const droppableCondition =
        !tilePartIsFinished && !answerIsFilledIn && name === answer?.name;

    return (
        <div
            styleName="wrapper"
            style={{
                gridTemplateColumns: `repeat(5, ${blockSize})`,
                gridTemplateRows: blockSize,
            }}
        >
            {tiles.map((tile, i) => {
                const idProp = `bar-${tile}-${id}`;
                return (
                    <DragNDropTile
                        key={idProp}
                        index={i}
                        droppableCondition={droppableCondition}
                        setTileBarState={setTileBarState}
                        id={idProp}
                        tilePartIsFinished={tilePartIsFinished}
                        tileBarState={tileBarState}
                    />
                );
            })}
        </div>
    );
}
